import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPageUrl } from '../utils/index.ts';
import { KnowledgeBase } from '../api/entities.js';
import { User } from '../api/entities.js';
import { Button } from "../components/ui/button.jsx";
import { Input } from "../components/ui/input.jsx";
import { Card, CardContent, CardFooter } from "../components/ui/card.jsx";
import { Badge } from '../components/ui/badge.jsx';
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs.jsx";
import { 
  Plus, 
  Search, 
  Book, 
  BookOpen, 
  BookMarked, 
  Bookmark, 
  Clock, 
  Tag, 
  MessageCircle, 
  Eye, 
  Star, 
  ThumbsUp, 
  Filter, 
  Grid3X3, 
  LayoutList, 
  FileText, 
  FileQuestion,
  ArrowUpRight,
  Share2,
  GripVertical,
  ArrowDownAZ,
  ArrowUpZA,
  Calendar,
  Edit3,
  Sparkles
} from 'lucide-react';
import { safeFormatDate, DATE_FORMATS } from '../utils/dateHelpers.js';
import { ScrollArea } from "../components/ui/scroll-area.jsx";
import { Skeleton } from "../components/ui/skeleton.jsx";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar.jsx";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select.jsx";
import { Separator } from "../components/ui/separator.jsx";
import { motion, AnimatePresence } from "framer-motion";

export default function Knowledge() {
  const navigate = useNavigate();
  const [articles, setArticles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [sortOption, setSortOption] = useState('recent'); // 'recent', 'title', 'views'
  const [currentUser, setCurrentUser] = useState(null);
  const [pinnedArticles, setPinnedArticles] = useState([]);
  const [popularArticles, setPopularArticles] = useState([]);
  const [featuredCategory, setFeaturedCategory] = useState(null);
  const [recentlyViewed, setRecentlyViewed] = useState([]);

  useEffect(() => {
    loadUser();
    loadArticles();
  }, []);

  const loadUser = async () => {
    try {
      const userData = await User.me();
      setCurrentUser(userData);
      
      // Carregar artigos recentemente visualizados do usuário
      if (userData.preferences?.recentlyViewedArticles) {
        setRecentlyViewed(userData.preferences.recentlyViewedArticles);
      } else {
        // Fallback: carregar do localStorage se não tiver no servidor
        try {
          const localViewed = localStorage.getItem('recentlyViewedArticles');
          if (localViewed) {
            const parsed = JSON.parse(localViewed);
            setRecentlyViewed(parsed);
            console.log('📱 Artigos visualizados carregados do localStorage');
          }
        } catch (localError) {
          console.warn('Erro ao carregar dados locais:', localError);
        }
      }
    } catch (error) {
      console.error('Erro ao carregar dados do usuário:', error);
      
      // Se falhar completamente, tentar carregar do localStorage
      try {
        const localViewed = localStorage.getItem('recentlyViewedArticles');
        if (localViewed) {
          const parsed = JSON.parse(localViewed);
          setRecentlyViewed(parsed);
          console.log('📱 Artigos visualizados carregados do localStorage (fallback total)');
        }
      } catch (localError) {
        console.warn('Erro ao carregar dados locais como fallback:', localError);
      }
    }
  };

  const loadArticles = async () => {
    setIsLoading(true);
    try {
      const data = await KnowledgeBase.list({}, { order: { column: 'created_at', ascending: false } });
      setArticles(data);
      
      // Extract unique categories
      const uniqueCategories = [...new Set(data.map(article => article.category))];
      setCategories(uniqueCategories);
      
      // Set featured category (the one with most articles)
      const categoryCounts = uniqueCategories.map(category => ({
        category,
        count: data.filter(article => article.category === category).length
      }));
      
      const mostPopularCategory = categoryCounts.sort((a, b) => b.count - a.count)[0]?.category;
      setFeaturedCategory(mostPopularCategory);
      
      // Simular artigos "fixados" (normalmente seriam definidos por um administrador)
      setPinnedArticles(data.slice(0, 2));
      
      // Simular artigos populares (normalmente seriam baseados em visualizações)
      // Aqui estamos apenas pegando os 3 primeiros para demonstração
      setPopularArticles(data.slice(0, 3));
    } catch (error) {
      console.error('Error loading articles:', error);
    }
    setIsLoading(false);
  };

  // Função para remover tags HTML e limitar o texto
  const stripHtmlAndTruncate = (html, limit = 200) => {
    if (!html) return '';
    // Remove tags HTML
    const text = html.replace(/<\/?[^>]+(>|$)/g, '');
    // Limita o texto e adiciona reticências se necessário
    return text.length > limit ? text.substring(0, limit) + '...' : text;
  };

  const handleViewArticle = async (articleId) => {
    navigate(createPageUrl(`ViewKnowledge?id=${articleId}`));
    
    // Atualizar lista de artigos recentemente vistos (apenas para simulação)
    if (currentUser) {
      const article = articles.find(a => a.id === articleId);
      if (article) {
        const newRecentlyViewed = [
          { id: article.id, title: article.title, category: article.category, viewed_at: new Date().toISOString() },
          ...recentlyViewed.filter(a => a.id !== articleId)
        ].slice(0, 5);  // manter apenas os 5 mais recentes
        
        setRecentlyViewed(newRecentlyViewed);
        
        // Tentar salvar nas preferências do usuário (não crítico se falhar)
        try {
          const result = await User.updateMyUserData({
            preferences: {
              ...(currentUser?.preferences || {}),
              recentlyViewedArticles: newRecentlyViewed
            }
          });
          
          if (result) {
            console.log('✅ Preferências de artigos visualizados atualizadas no servidor');
          } else {
            // Fallback: salvar no localStorage
            try {
              localStorage.setItem('recentlyViewedArticles', JSON.stringify(newRecentlyViewed));
              console.log('📱 Preferências salvas localmente como fallback');
            } catch (localError) {
              console.warn('Não foi possível salvar nem no servidor nem localmente');
            }
          }
        } catch (error) {
          // Fallback: salvar no localStorage se o servidor falhar
          try {
            localStorage.setItem('recentlyViewedArticles', JSON.stringify(newRecentlyViewed));
            console.log('📱 Preferências salvas localmente (servidor indisponível)');
          } catch (localError) {
            console.warn('Não foi possível salvar preferências nem no servidor nem localmente');
          }
        }
      }
    }
  };

  const filteredArticles = articles.filter(article => {
    const matchesSearch = searchTerm === '' || 
      article.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (article.content && article.content.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (article.tags && article.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase())));
    
    const matchesCategory = selectedCategory === 'all' || article.category === selectedCategory;
    
    return matchesSearch && matchesCategory;
  });

  // Ordenar artigos
  const sortedArticles = [...filteredArticles].sort((a, b) => {
    if (sortOption === 'title') {
      return a.title.localeCompare(b.title);
    } else if (sortOption === 'views') {
      // Simulando contagem de visualizações (normalmente viria do banco de dados)
      return (b.views || 0) - (a.views || 0);
    } else {
      // Por padrão, ordenar por data de criação (mais recente primeiro)
      const dateA = a.created_at ? new Date(a.created_at) : new Date(0);
      const dateB = b.created_at ? new Date(b.created_at) : new Date(0);
      return dateB - dateA;
    }
  });

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between">
          <div>
            <Skeleton className="h-8 w-64 mb-2" />
            <Skeleton className="h-4 w-48" />
          </div>
          <Skeleton className="h-10 w-32" />
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="md:col-span-1">
            <Skeleton className="h-10 w-full mb-4" />
            <Skeleton className="h-8 w-full mb-2" />
            <Skeleton className="h-8 w-full mb-2" />
            <Skeleton className="h-8 w-full mb-2" />
            <Skeleton className="h-8 w-full mb-2" />
          </div>
          
          <div className="md:col-span-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Skeleton className="h-48 w-full" />
              <Skeleton className="h-48 w-full" />
              <Skeleton className="h-48 w-full" />
              <Skeleton className="h-48 w-full" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Componente para cartão de artigo (visualização em grade)
  const ArticleCard = ({ article, isPinned = false }) => {
    const categoryColor = {
      'Tutoriais': 'bg-blue-100 text-blue-800 hover:bg-blue-200',
      'FAQ': 'bg-amber-100 text-amber-800 hover:bg-amber-200',
      'Guias': 'bg-emerald-100 text-emerald-800 hover:bg-emerald-200',
      'Suporte': 'bg-purple-100 text-purple-800 hover:bg-purple-200',
      'Produtos': 'bg-pink-100 text-pink-800 hover:bg-pink-200',
    }[article.category] || 'bg-gray-100 text-gray-800 hover:bg-gray-200';

    const categoryIcon = {
      'Tutoriais': <Book className="w-4 h-4" />,
      'FAQ': <FileQuestion className="w-4 h-4" />,
      'Guias': <FileText className="w-4 h-4" />,
      'Suporte': <MessageCircle className="w-4 h-4" />,
      'Produtos': <Grid3X3 className="w-4 h-4" />,
    }[article.category] || <BookMarked className="w-4 h-4" />;

    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <Card 
          className={`cursor-pointer h-full overflow-hidden ${isPinned ? 'ring-2 ring-primary/20 bg-primary/5' : 'hover:shadow-md'} transition-all`}
          onClick={() => handleViewArticle(article.id)}
        >
          {isPinned && (
            <div className="bg-primary text-white text-xs font-medium px-3 py-1 flex items-center justify-center">
              <Bookmark className="w-3 h-3 mr-1" /> Artigo Destacado
            </div>
          )}
          <CardContent className="p-5 space-y-4">
            <div className="flex justify-between items-start">
              <Badge className={`px-2 py-1 ${categoryColor} flex items-center gap-1`}>
                {categoryIcon}
                {article.category}
              </Badge>
              {article.views && (
                <div className="text-xs text-muted-foreground flex items-center">
                  <Eye className="w-3 h-3 mr-1" />
                  {article.views} visualizações
                </div>
              )}
            </div>
            
            <div>
              <h3 className="font-semibold text-lg line-clamp-2 mb-2">{article.title}</h3>
              <p className="text-sm text-muted-foreground line-clamp-3">
                {stripHtmlAndTruncate(article.content, 150)}
              </p>
            </div>
          </CardContent>
          
          <CardFooter className="px-5 py-3 border-t bg-muted/20 flex items-center justify-between">
            <div className="flex items-center text-xs text-muted-foreground">
              <Calendar className="w-3 h-3 mr-1" />
              {safeFormatDate(article.created_at, DATE_FORMATS.SHORT_DATE)}
            </div>
            
            <div className="flex gap-2">
              {article.tags && article.tags.slice(0, 2).map((tag, i) => (
                <Badge key={i} variant="outline" className="text-xs">
                  {tag}
                </Badge>
              ))}
              {article.tags && article.tags.length > 2 && (
                <Badge variant="outline" className="text-xs">
                  +{article.tags.length - 2}
                </Badge>
              )}
            </div>
          </CardFooter>
        </Card>
      </motion.div>
    );
  };

  // Componente para item de artigo (visualização em lista)
  const ArticleListItem = ({ article }) => {
    return (
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.2 }}
      >
        <Card 
          className="cursor-pointer hover:bg-muted/10 transition-colors mb-2"
          onClick={() => handleViewArticle(article.id)}
        >
          <CardContent className="p-4 flex items-start gap-4">
            <div className="hidden sm:flex items-center justify-center h-10 w-10 rounded-full bg-primary/10 text-primary">
              <BookOpen className="h-5 w-5" />
            </div>
            
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <h3 className="font-medium">{article.title}</h3>
                <Badge className="text-xs" variant="secondary">
                  {article.category}
                </Badge>
              </div>
              
              <p className="text-sm text-muted-foreground line-clamp-2 mb-2">
                {stripHtmlAndTruncate(article.content, 120)}
              </p>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <span className="flex items-center">
                    <Calendar className="w-3 h-3 mr-1" />
                    {safeFormatDate(article.created_at, DATE_FORMATS.SHORT_DATE)}
                  </span>
                  
                  {article.views && (
                    <span className="flex items-center">
                      <Eye className="w-3 h-3 mr-1" />
                      {article.views} visualizações
                    </span>
                  )}
                </div>
                
                <div className="flex gap-1">
                  {article.tags && article.tags.slice(0, 2).map((tag, i) => (
                    <Badge key={i} variant="outline" className="text-xs">
                      {tag}
                    </Badge>
                  ))}
                  {article.tags && article.tags.length > 2 && (
                    <Badge variant="outline" className="text-xs">
                      +{article.tags.length - 2}
                    </Badge>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <BookOpen className="h-8 w-8 text-primary" />
            Base de Conhecimento
          </h1>
          <p className="text-muted-foreground mt-1">
            Explore e pesquise artigos, tutoriais e soluções
          </p>
        </div>
        <div className="flex gap-2">
          <div className="flex gap-1 border rounded-md">
            <Button 
              variant="ghost" 
              size="sm" 
              className={`rounded-none ${viewMode === 'grid' ? 'bg-muted' : ''}`}
              onClick={() => setViewMode('grid')}
            >
              <Grid3X3 className="h-4 w-4" />
            </Button>
            <Button 
              variant="ghost" 
              size="sm" 
              className={`rounded-none ${viewMode === 'list' ? 'bg-muted' : ''}`}
              onClick={() => setViewMode('list')}
            >
              <LayoutList className="h-4 w-4" />
            </Button>
          </div>
          <Button
            onClick={() => navigate(createPageUrl('NewKnowledge'))}
            className="bg-primary"
          >
            <Plus className="w-4 h-4 mr-2" />
            Novo Artigo
          </Button>
        </div>
      </div>

      {/* Banner destacado para categorias populares */}
      {featuredCategory && (
        <Card className="bg-gradient-to-r from-primary/10 to-primary/5 border-none overflow-hidden">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row gap-6 items-center">
              <div className="flex-1 space-y-3">
                <Badge className="bg-primary/20 text-primary hover:bg-primary/30 px-3 py-1">
                  CATEGORIA EM DESTAQUE
                </Badge>
                <h2 className="text-2xl font-bold">{featuredCategory}</h2>
                <p className="text-muted-foreground">
                  Explore nossa coleção de {filteredArticles.filter(a => a.category === featuredCategory).length} artigos 
                  sobre {featuredCategory.toLowerCase()} para encontrar soluções e tirar suas dúvidas.
                </p>
                <Button 
                  variant="outline" 
                  className="mt-2"
                  onClick={() => setSelectedCategory(featuredCategory)}
                >
                  Ver Artigos <ArrowUpRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
              
              <div className="hidden md:flex items-center justify-center bg-primary/10 text-primary h-32 w-32 rounded-full">
                <Sparkles className="h-12 w-12" />
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid gap-6 md:grid-cols-4">
        <div className="md:col-span-1 space-y-6">
          <Card>
            <CardContent className="p-4">
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Pesquisar artigos..."
                  className="pl-8"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              
              <Separator className="my-4" />
              
              <div className="space-y-1">
                <h3 className="font-medium text-sm mb-2 flex items-center">
                  <Tag className="w-4 h-4 mr-2" />
                  CATEGORIAS
                </h3>
                <Button
                  variant={selectedCategory === 'all' ? "default" : "ghost"}
                  className="w-full justify-start"
                  onClick={() => setSelectedCategory('all')}
                >
                  Todas as categorias
                  <Badge className="ml-auto" variant="secondary">
                    {articles.length}
                  </Badge>
                </Button>
                {categories.map((category) => (
                  <Button
                    key={category}
                    variant={selectedCategory === category ? "default" : "ghost"}
                    className="w-full justify-start"
                    onClick={() => setSelectedCategory(category)}
                  >
                    {category}
                    <Badge className="ml-auto" variant="secondary">
                      {articles.filter(a => a.category === category).length}
                    </Badge>
                  </Button>
                ))}
              </div>
              
              <Separator className="my-4" />
              
              <div className="space-y-1">
                <h3 className="font-medium text-sm mb-2 flex items-center">
                  <Filter className="w-4 h-4 mr-2" />
                  ORDENAR POR
                </h3>
                <Select value={sortOption} onValueChange={setSortOption}>
                  <SelectTrigger>
                    <SelectValue placeholder="Ordenar por" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="recent">
                      <div className="flex items-center">
                        <Clock className="w-4 h-4 mr-2" />
                        Mais recentes
                      </div>
                    </SelectItem>
                    <SelectItem value="title">
                      <div className="flex items-center">
                        <ArrowDownAZ className="w-4 h-4 mr-2" />
                        Alfabética (A-Z)
                      </div>
                    </SelectItem>
                    <SelectItem value="views">
                      <div className="flex items-center">
                        <Eye className="w-4 h-4 mr-2" />
                        Mais vistos
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>
          
          {/* Artigos recentemente visualizados */}
          {recentlyViewed.length > 0 && (
            <Card>
              <CardContent className="p-4 space-y-4">
                <h3 className="font-medium flex items-center">
                  <Clock className="w-4 h-4 mr-2 text-muted-foreground" />
                  Visualizados Recentemente
                </h3>
                <ScrollArea className="h-60 pr-4">
                  {recentlyViewed.map((article) => (
                    <div 
                      key={article.id} 
                      className="py-2 text-sm cursor-pointer hover:bg-muted/50 px-2 rounded-md transition-colors flex items-center"
                      onClick={() => handleViewArticle(article.id)}
                    >
                      <div className="mr-2 text-primary">
                        <BookOpen className="h-4 w-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium truncate">{article.title}</div>
                        <div className="text-xs text-muted-foreground">{article.category}</div>
                      </div>
                    </div>
                  ))}
                </ScrollArea>
              </CardContent>
            </Card>
          )}
          
          {/* Artigos populares */}
          <Card>
            <CardContent className="p-4 space-y-4">
              <h3 className="font-medium flex items-center">
                <ThumbsUp className="w-4 h-4 mr-2 text-muted-foreground" />
                Artigos Populares
              </h3>
              <ScrollArea className="h-60">
                {popularArticles.map((article) => (
                  <div 
                    key={article.id} 
                    className="py-2 text-sm cursor-pointer hover:bg-muted/50 px-2 rounded-md transition-colors"
                    onClick={() => handleViewArticle(article.id)}
                  >
                    <div className="font-medium">{article.title}</div>
                    <div className="text-xs text-muted-foreground flex items-center justify-between">
                      <span>{article.category}</span>
                      <span className="flex items-center">
                        <Eye className="w-3 h-3 mr-1" />
                        {article.views || Math.floor(Math.random() * 100) + 20}
                      </span>
                    </div>
                  </div>
                ))}
              </ScrollArea>
            </CardContent>
          </Card>
        </div>

        <div className="md:col-span-3 space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold">
              {selectedCategory === 'all' 
                ? 'Todos os Artigos' 
                : `Artigos em ${selectedCategory}`}
              <Badge variant="secondary" className="ml-2">
                {sortedArticles.length}
              </Badge>
            </h2>
            
            {searchTerm && (
              <Badge variant="outline" className="flex items-center gap-1">
                <Search className="h-3 w-3" />
                Resultados para: "{searchTerm}"
              </Badge>
            )}
          </div>
          
          {pinnedArticles.length > 0 && selectedCategory === 'all' && !searchTerm && (
            <div className="mb-6">
              <h3 className="text-sm font-medium text-muted-foreground mb-3 flex items-center">
                <Bookmark className="h-4 w-4 mr-2" />
                ARTIGOS FIXADOS
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {pinnedArticles.map((article) => (
                  <ArticleCard key={article.id} article={article} isPinned={true} />
                ))}
              </div>
            </div>
          )}
          
          <AnimatePresence>
            {viewMode === 'grid' ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {sortedArticles.map((article) => (
                  <ArticleCard key={article.id} article={article} />
                ))}
              </div>
            ) : (
              <div className="space-y-2">
                {sortedArticles.map((article) => (
                  <ArticleListItem key={article.id} article={article} />
                ))}
              </div>
            )}
          </AnimatePresence>
          
          {sortedArticles.length === 0 && (
            <Card>
              <CardContent className="py-8 text-center text-muted-foreground">
                <FileQuestion className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
                <h3 className="text-lg font-medium mb-2">Nenhum artigo encontrado</h3>
                <p>Não encontramos artigos que correspondam aos seus filtros de pesquisa.</p>
                <Button
                  variant="outline"
                  className="mt-4"
                  onClick={() => {
                    setSearchTerm('');
                    setSelectedCategory('all');
                  }}
                >
                  Limpar filtros
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}